import { useCallback, useEffect, useState } from "react";
import { TaskCard } from "../components/TaskCard";
import { TaskFilter } from "../components/TaskFilter";
import { TaskForm } from "../components/TaskForm";
import type { ApiResponse, CreateTaskInput, Task, TaskFilter as TaskFilterType } from "../types";

export function TaskListPage() {
	const [tasks, setTasks] = useState<Task[]>([]);
	const [filter, setFilter] = useState<TaskFilterType>({});
	const [isLoading, setIsLoading] = useState(true);
	const [error, setError] = useState<string | null>(null);
	const [isCreating, setIsCreating] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const fetchTasks = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const params = new URLSearchParams();
			if (filter.status) params.append("status", filter.status);
			if (filter.priority) params.append("priority", filter.priority);
			if (filter.search) params.append("search", filter.search);

			const response = await fetch(`/api/tasks?${params.toString()}`);
			const data: ApiResponse<Task[]> = await response.json();

			if (data.success && data.data) {
				setTasks(data.data);
			} else {
				setError(data.error || "タスクの取得に失敗しました");
			}
		} catch (err) {
			setError("サーバーとの通信に失敗しました");
			console.error("Failed to fetch tasks:", err);
		} finally {
			setIsLoading(false);
		}
	}, [filter]);

	useEffect(() => {
		fetchTasks();
	}, [fetchTasks]);

	const handleCreateTask = async (data: CreateTaskInput) => {
		setIsSubmitting(true);
		setError(null);

		try {
			const response = await fetch("/api/tasks", {
				method: "POST",
				headers: { "Content-Type": "application/json" },
				body: JSON.stringify(data),
			});
			const result: ApiResponse<Task> = await response.json();

			if (result.success && result.data) {
				const newTask = result.data;
				setTasks((prev) => [newTask, ...prev]);
				setIsCreating(false);
			} else {
				setError(result.error || "タスクの作成に失敗しました");
			}
		} catch (err) {
			setError("サーバーとの通信に失敗しました");
			console.error("Failed to create task:", err);
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleDeleteTask = async (id: string) => {
		try {
			const response = await fetch(`/api/tasks/${id}`, {
				method: "DELETE",
			});
			const result: ApiResponse<null> = await response.json();

			if (result.success) {
				setTasks((prev) => prev.filter((task) => task.id !== id));
			} else {
				setError(result.error || "タスクの削除に失敗しました");
			}
		} catch (err) {
			setError("サーバーとの通信に失敗しました");
			console.error("Failed to delete task:", err);
		}
	};

	return (
		<div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
			<div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
				<h1 className="text-2xl font-bold text-gray-900">タスク一覧</h1>
				{!isCreating && (
					<button type="button" onClick={() => setIsCreating(true)} className="btn-primary">
						<svg
							className="w-5 h-5 mr-2"
							fill="none"
							stroke="currentColor"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<path
								strokeLinecap="round"
								strokeLinejoin="round"
								strokeWidth={2}
								d="M12 4v16m8-8H4"
							/>
						</svg>
						新規タスク
					</button>
				)}
			</div>

			{error && (
				<div
					className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700"
					role="alert"
				>
					<div className="flex items-center gap-2">
						<svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
							<path
								fillRule="evenodd"
								d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
								clipRule="evenodd"
							/>
						</svg>
						<span>{error}</span>
					</div>
				</div>
			)}

			{isCreating && (
				<div className="card p-6 mb-6">
					<h2 className="text-lg font-semibold text-gray-900 mb-4">新規タスク作成</h2>
					<TaskForm
						onSubmit={handleCreateTask}
						onCancel={() => setIsCreating(false)}
						isSubmitting={isSubmitting}
					/>
				</div>
			)}

			<div className="mb-6">
				<TaskFilter filter={filter} onFilterChange={setFilter} />
			</div>

			{isLoading ? (
				<div className="flex items-center justify-center py-12">
					<div className="flex flex-col items-center gap-4">
						<svg
							className="animate-spin h-8 w-8 text-primary-600"
							fill="none"
							viewBox="0 0 24 24"
							aria-hidden="true"
						>
							<circle
								className="opacity-25"
								cx="12"
								cy="12"
								r="10"
								stroke="currentColor"
								strokeWidth="4"
							/>
							<path
								className="opacity-75"
								fill="currentColor"
								d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
							/>
						</svg>
						<p className="text-gray-600">読み込み中...</p>
					</div>
				</div>
			) : tasks.length === 0 ? (
				<div className="card p-12 text-center">
					<svg
						className="w-16 h-16 mx-auto text-gray-400 mb-4"
						fill="none"
						stroke="currentColor"
						viewBox="0 0 24 24"
						aria-hidden="true"
					>
						<path
							strokeLinecap="round"
							strokeLinejoin="round"
							strokeWidth={1.5}
							d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"
						/>
					</svg>
					<h3 className="text-lg font-medium text-gray-900 mb-2">
						{filter.status || filter.priority || filter.search
							? "条件に一致するタスクがありません"
							: "タスクがありません"}
					</h3>
					<p className="text-gray-600 mb-6">
						{filter.status || filter.priority || filter.search
							? "フィルター条件を変更してください"
							: "新しいタスクを作成して始めましょう"}
					</p>
					{!isCreating && !(filter.status || filter.priority || filter.search) && (
						<button type="button" onClick={() => setIsCreating(true)} className="btn-primary">
							最初のタスクを作成
						</button>
					)}
				</div>
			) : (
				<div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
					{tasks.map((task) => (
						<TaskCard key={task.id} task={task} onDelete={handleDeleteTask} />
					))}
				</div>
			)}

			{!isLoading && tasks.length > 0 && (
				<div className="mt-6 text-center text-sm text-gray-500">{tasks.length}件のタスク</div>
			)}
		</div>
	);
}
